import axios from 'axios';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { Feature, FeatureCollection, LineString } from 'geojson';

import { fetchGeometryBatch } from '@/api/endpoints/routes';
import type {
  BatchGeometryFeature,
  RouteVariantKey,
} from '@/types/routes';

export type VariantCollections = Readonly<
  Record<RouteVariantKey, FeatureCollection<LineString>>
>;

interface UseVisibleRoutesGeometryOptions {
  readonly routeIds: ReadonlySet<string>;
  readonly activeVariants: ReadonlySet<RouteVariantKey>;
  /**
   * Per-route toggles: for each `(routeId, variant)` combination that
   * should actually render, return `true`. Features whose
   * `(route_id, variant)` is toggled off are filtered out of the
   * FeatureCollection even if the batch API returned them.
   */
  readonly isVariantEnabled: (routeId: string, variant: RouteVariantKey) => boolean;
  /** Revision counter: bumping re-fetches even if routeIds didn't change. */
  readonly revision: number;
}

interface UseVisibleRoutesGeometryResult {
  readonly collections: VariantCollections;
  readonly loading: boolean;
  readonly error: string | null;
}

export function useVisibleRoutesGeometry(
  options: UseVisibleRoutesGeometryOptions,
): UseVisibleRoutesGeometryResult {
  const { routeIds, activeVariants, isVariantEnabled, revision } = options;
  const [raw, setRaw] = useState<
    ReadonlyMap<RouteVariantKey, readonly BatchGeometryFeature[]>
  >(new Map());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const controllerRef = useRef<AbortController | null>(null);

  const idsKey = useMemo(
    () => [...routeIds].sort().join(','),
    [routeIds],
  );
  const variantsKey = useMemo(
    () => [...activeVariants].sort().join(','),
    [activeVariants],
  );

  useEffect(() => {
    controllerRef.current?.abort();

    if (routeIds.size === 0 || activeVariants.size === 0) {
      setRaw(new Map());
      setLoading(false);
      setError(null);
      return undefined;
    }

    const controller = new AbortController();
    controllerRef.current = controller;
    setLoading(true);
    setError(null);

    fetchGeometryBatch(
      {
        route_ids: [...routeIds],
        variants: [...activeVariants],
      },
      controller.signal,
    )
      .then((response) => {
        if (controller.signal.aborted) return;
        const next = new Map<RouteVariantKey, readonly BatchGeometryFeature[]>();
        for (const collection of response.collections) {
          next.set(collection.variant, collection.features);
        }
        setRaw(next);
        setLoading(false);
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted || axios.isCancel(err)) return;
        setError(err instanceof Error ? err.message : 'unknown error');
        setLoading(false);
      });

    return () => controller.abort();
  }, [idsKey, variantsKey, revision, routeIds, activeVariants]);

  const collections = useMemo<VariantCollections>(() => {
    const out: Record<RouteVariantKey, FeatureCollection<LineString>> = {
      gis: { type: 'FeatureCollection', features: [] },
      iteko: { type: 'FeatureCollection', features: [] },
      barrier_aware: { type: 'FeatureCollection', features: [] },
    };
    for (const variant of activeVariants) {
      const source = raw.get(variant) ?? [];
      out[variant] = {
        type: 'FeatureCollection',
        features: source
          .filter((f) => isVariantEnabled(f.properties.route_id, variant))
          .map((f) => toLineFeature(f)),
      };
    }
    return out;
  }, [raw, activeVariants, isVariantEnabled]);

  return { collections, loading, error };
}

function toLineFeature(
  feature: BatchGeometryFeature,
): Feature<LineString> {
  return {
    type: 'Feature',
    id: feature.id,
    geometry: {
      type: 'LineString',
      coordinates: feature.geometry.coordinates.map(([lon, lat]) => [lon, lat]),
    },
    properties: {
      route_id: feature.properties.route_id,
      variant: feature.properties.variant,
      length_km: feature.properties.length_km,
      duration_min: feature.properties.duration_min,
      applied_barriers: [...feature.properties.applied_barriers],
      residual_barriers: [...feature.properties.residual_barriers],
      stale: feature.properties.stale,
    },
  };
}
